/* ARIA Motion runtime v1.0 (2026-10-11)
 *
 * One file, attribute driven, GSAP 3 + ScrollTrigger. Every moving element
 * declares WHAT it does (data-m) and WHY (data-m-job). The X-ray mode and
 * motion_check.py read both, so a tween without a job is a defect, never
 * decoration. Standard: Skills/website-builder/motion/STANDARD.md.
 *
 *   <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/gsap.min.js"></script>
 *   <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/ScrollTrigger.min.js"></script>
 *   <script src="motion.js" defer></script>
 *   (the two GSAP tags are optional: motion.js loads them itself when absent)
 *
 * Primitives (data-m="..."):
 *   reveal | reveal-l | reveal-r | reveal-s   fade-in on scroll (up / from left / from right / scale)
 *   stagger        children reveal one after another (data-m-gap, seconds, default 0.08)
 *   count          number counts up to data-m-to (data-m-dec decimals, data-m-suffix, data-m-prefix)
 *   draw           SVG path/line/circle strokes draw on as the element enters
 *   marquee        content scrolls sideways forever, pauses on hover (data-m-speed px/s, default 60)
 *   parallax       moves slower than the page (data-m-speed, fraction of scroll, default 0.2)
 *   scrub          a <video> whose currentTime follows the scroll through its parent section
 *   float          perpetual gentle loop; first frame is the last frame by construction
 *   magnetic       a button that leans toward the pointer within data-m-radius px (default 80), never more than data-m-shift px (default 5)
 *   tilt           a card tilts in 3D toward the pointer
 *   glide          a container whose highlight panel glides between hovered children (bento glide)
 *   buddy          a character that follows the pointer with lag, flips, idles, and reacts on click
 *   typewrite      text types itself in when it enters (data-m-cps characters per second, default 40)
 *   header         a nav that starts transparent and turns solid after data-m-after px (default 40): toggles .is-solid
 *   css            no tween: the motion lives in the page's own CSS (a keyframes marquee); declared so the X-ray and the gate see it
 *   bespoke        no tween here: the page's own script owns it (a configurator tally); declared so the X-ray and the gate see it
 *   graphic        the page's one drawn piece (rule 8): data-m-graphic names the CATALOG.md component, the page's script draws it; X-ray green
 *   turn           drag (or arrow keys) to turn a <video>: horizontal drag maps to currentTime, data-m-turns px per full pass (default 600)
 *
 * scrub options: data-m-pin="1.5" pins the section for 1.5 viewports while the video scrubs (a hero rotation);
 *   data-m-pin-target=".sv-top" pins the closest matching ancestor instead of the section (header + hero as one block).
 * parallax options: data-m-speed (fraction of height, centered), data-m-scale (enlarge an image in a clipped box).
 *
 * Footage registry (what the X-ray labels in amber):
 *   data-footage="ai|photo|video|3d"  data-footage-credit="Design OS · Wan 3.0 · $0.25"
 *
 * Modes: html[data-motion="on|off|xray"]. URL ?motion=off or ?xray=1 also work.
 * window.ARIAMotion = { init, revert, mode(m), count() }.
 */
(function () {
  'use strict';
  var html = document.documentElement;
  html.classList.add('m-js');

  var GSAP_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/';
  var EASE = 'power3.out';
  var mm = null;            // gsap.matchMedia context (reverted on Off / reduced motion)
  var booted = false;
  var cleanups = [];        // pointer listeners etc. that matchMedia does not own

  function num(el, name, dflt) {
    var v = el.getAttribute('data-m-' + name);
    if (v === null || v === '') return dflt;
    var n = parseFloat(v);
    return isNaN(n) ? dflt : n;
  }
  function str(el, name, dflt) {
    var v = el.getAttribute('data-m-' + name);
    return v === null ? dflt : v;
  }
  function all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function finePointer() { return window.matchMedia('(pointer: fine)').matches; }

  /* ---------- loader ---------- */
  function loadScript(src, cb) {
    var s = document.createElement('script');
    s.src = src; s.async = false; s.onload = cb;
    s.onerror = function () { console.error('[motion] failed to load ' + src); };
    document.head.appendChild(s);
  }
  function ensureGsap(cb) {
    if (window.gsap && window.ScrollTrigger) return cb();
    if (window.gsap) return loadScript(GSAP_CDN + 'ScrollTrigger.min.js', cb);
    loadScript(GSAP_CDN + 'gsap.min.js', function () { loadScript(GSAP_CDN + 'ScrollTrigger.min.js', cb); });
  }

  /* ---------- primitives ---------- */
  var P = {};

  P.reveal = function (el, dir) {
    var dist = num(el, 'dist', 32);
    var from = { opacity: 0 };
    if (dir === 'l') from.x = -dist; else if (dir === 'r') from.x = dist;
    else if (dir === 's') from.scale = 0.95; else from.y = dist;
    gsap.fromTo(el, from, {
      opacity: 1, x: 0, y: 0, scale: 1,
      duration: num(el, 'dur', dir === 's' ? 0.7 : 0.8), delay: num(el, 'delay', 0),
      ease: dir === 's' ? 'back.out(1.2)' : EASE,
      scrollTrigger: { trigger: el, start: str(el, 'start', 'top 88%'), once: true },
      clearProps: 'transform'
    });
  };
  P['reveal-l'] = function (el) { P.reveal(el, 'l'); };
  P['reveal-r'] = function (el) { P.reveal(el, 'r'); };
  P['reveal-s'] = function (el) { P.reveal(el, 's'); };

  P.stagger = function (el) {
    var kids = Array.prototype.slice.call(el.children);
    if (!kids.length) return;
    gsap.fromTo(kids, { opacity: 0, y: num(el, 'dist', 24) }, {
      opacity: 1, y: 0, duration: num(el, 'dur', 0.7), stagger: num(el, 'gap', 0.08), ease: EASE,
      scrollTrigger: { trigger: el, start: str(el, 'start', 'top 85%'), once: true }, clearProps: 'transform'
    });
  };

  P.count = function (el) {
    var to = num(el, 'to', parseFloat(el.textContent) || 0);
    var dec = num(el, 'dec', 0), pre = str(el, 'prefix', ''), suf = str(el, 'suffix', '');
    var sep = str(el, 'sep', ',');
    var obj = { v: 0 };
    function fmt(v) {
      var s = v.toFixed(dec);
      if (sep) { var parts = s.split('.'); parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep); s = parts.join('.'); }
      return pre + s + suf;
    }
    el.textContent = fmt(0);
    ScrollTrigger.create({
      trigger: el, start: str(el, 'start', 'top 85%'), once: true,
      onEnter: function () {
        gsap.to(obj, { v: to, duration: num(el, 'dur', 1.8), ease: 'power2.out', onUpdate: function () { el.textContent = fmt(obj.v); } });
      }
    });
  };

  P.draw = function (el) {
    var paths = el.tagName.toLowerCase() === 'svg' ? all('path, line, circle, polyline, rect, ellipse', el) : all('svg path, svg line, svg circle, svg polyline, svg rect, svg ellipse', el);
    if (!paths.length) return;
    paths.forEach(function (p) {
      var len = 1000;
      try { len = p.getTotalLength ? p.getTotalLength() : 1000; } catch (e) {}
      p.style.strokeDasharray = len; p.style.strokeDashoffset = len;
    });
    gsap.to(paths, {
      strokeDashoffset: 0, duration: num(el, 'dur', 1.2), stagger: num(el, 'gap', 0.15), ease: 'power2.inOut',
      delay: num(el, 'delay', 0), scrollTrigger: { trigger: el, start: str(el, 'start', 'top 85%'), once: true }
    });
  };

  P.marquee = function (el) {
    var speed = num(el, 'speed', 60); // px per second
    var track = el.querySelector('[data-m-track]') || el.firstElementChild;
    if (!track) return;
    if (!track.getAttribute('data-m-cloned')) {
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true'); clone.setAttribute('data-m-clone', '1');
      el.appendChild(clone); track.setAttribute('data-m-cloned', '1');
    }
    el.style.display = 'flex'; el.style.overflow = 'hidden'; el.style.whiteSpace = 'nowrap';
    var w = track.offsetWidth + parseFloat(getComputedStyle(track).marginRight || 0);
    if (!w) return;
    var tw = gsap.to(el.children, { x: -w, duration: w / speed, ease: 'none', repeat: -1 });
    el.addEventListener('mouseenter', function () { tw.pause(); });
    el.addEventListener('mouseleave', function () { tw.play(); });
  };

  P.parallax = function (el) {
    var f = num(el, 'speed', 0.2), sc = num(el, 'scale', 1);
    // centered: -f/2 to +f/2 of the element's height across its parent's pass through the viewport;
    // data-m-scale="1.2" enlarges an image inside an overflow-hidden box so the travel never shows an edge
    gsap.fromTo(el, { yPercent: -50 * f, scale: sc }, { yPercent: 50 * f, scale: sc, ease: 'none', scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true } });
  };

  P.scrub = function (el) {
    var video = el.tagName.toLowerCase() === 'video' ? el : el.querySelector('video');
    if (!video) return;
    var section = el.closest(str(el, 'pin-target', 'section')) || el.closest('section') || el.parentElement;
    var pin = num(el, 'pin', 0);
    video.pause(); video.muted = true;
    // 2026-10-13: the clip waits (markup preload="none") until the page has loaded and gone idle, or the first scroll
    // of the section, so the poster (the LCP) never shares the network with it. No video.load(): it restarted source
    // selection and fetched the clip a second time; a preload change alone starts the fetch.
    // the clip's own first frame at rest (a poster JPEG can carry different levels than the decoded video); skipped once a scroll has moved it
    function firstFrame() { if (cur > 0.001) return; try { video.currentTime = 0.001; } catch (e) {} }
    var warmed = false;
    function warm() {
      if (warmed) return; warmed = true;
      video.preload = 'auto';
      if (video.readyState >= 1) firstFrame(); else video.addEventListener('loadedmetadata', firstFrame, { once: true });
    }
    function idle() { if (window.requestIdleCallback) requestIdleCallback(warm, { timeout: 3000 }); else setTimeout(warm, 1500); }
    if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
    var target = 0, cur = 0, raf = null;
    function tick() {
      cur += (target - cur) * 0.15;
      if (video.duration) { try { video.currentTime = cur * video.duration; } catch (e) {} }
      if (Math.abs(target - cur) > 0.001) raf = requestAnimationFrame(tick); else raf = null;
    }
    var cfg = {
      trigger: section, start: str(el, 'start', 'top top'), end: pin ? '+=' + Math.round(pin * 100) + '%' : str(el, 'end', 'bottom top'), scrub: true,
      onUpdate: function (st) { warm(); target = st.progress; if (!raf) raf = requestAnimationFrame(tick); }
    };
    if (pin) { cfg.pin = true; cfg.pinSpacing = true; cfg.anticipatePin = 1; } // pinSpacing explicit: GSAP turns it OFF when the pinned element's parent is display:flex (a flex-column body), and the next section then slides under the hero
    ScrollTrigger.create(cfg);
  };

  P.header = function (el) {
    var after = num(el, 'after', 40);
    function check() { el.classList.toggle('is-solid', window.scrollY > after); }
    window.addEventListener('scroll', check, { passive: true }); check();
    cleanups.push(function () { window.removeEventListener('scroll', check); el.classList.remove('is-solid'); });
  };

  P.css = function () { /* declared only: the page's own CSS animates it */ };
  P.bespoke = function () { /* declared only: the page's own script owns it */ };
  P.graphic = function () { /* declared only: the page's own script draws it; data-m-graphic names the catalog piece */ };

  P.turn = function (el) {
    var video = el.tagName.toLowerCase() === 'video' ? el : el.querySelector('video');
    if (!video) return;
    var px = num(el, 'turns', 600); // pixels of drag for one full pass of the clip
    video.pause(); video.muted = true; video.preload = 'auto';
    try { video.load(); } catch (e) {}
    var dur = 0, pos = 0, dragging = false, lastX = 0, target = 0, raf = null;
    function clampLoop(t) { if (!dur) return 0; t = t % dur; return t < 0 ? t + dur : t; }
    function tick() { pos += (target - pos) * 0.25; try { video.currentTime = clampLoop(pos); } catch (e) {} if (Math.abs(target - pos) > 0.002) raf = requestAnimationFrame(tick); else raf = null; }
    function nudge(dt) { target += dt; if (!raf) raf = requestAnimationFrame(tick); }
    video.addEventListener('loadedmetadata', function () { dur = video.duration; target = pos = num(el, 'start', 0); try { video.currentTime = pos || 0.001; } catch (e) {} }, { once: true });
    el.style.touchAction = 'pan-y'; el.style.cursor = 'grab'; el.setAttribute('tabindex', '0');
    function down(e) { dragging = true; lastX = e.clientX; el.style.cursor = 'grabbing'; el.setPointerCapture && el.setPointerCapture(e.pointerId); }
    function move(e) { if (!dragging || !dur) return; var dx = e.clientX - lastX; lastX = e.clientX; nudge(-dx / px * dur); }
    function up() { dragging = false; el.style.cursor = 'grab'; }
    function key(e) { if (!dur) return; if (e.key === 'ArrowLeft') nudge(dur / 24); if (e.key === 'ArrowRight') nudge(-dur / 24); }
    el.addEventListener('pointerdown', down); el.addEventListener('pointermove', move); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up); el.addEventListener('keydown', key);
    el.turnTo = function (t) { target = t; if (!raf) raf = requestAnimationFrame(tick); };
    cleanups.push(function () { el.removeEventListener('pointerdown', down); el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); el.removeEventListener('keydown', key); });
  };

  P.float = function (el) {
    var amp = num(el, 'dist', 10), dur = num(el, 'dur', 3);
    gsap.to(el, { y: -amp, rotation: num(el, 'rot', 0), duration: dur, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: num(el, 'delay', 0) });
  };

  P.magnetic = function (el) {
    if (!finePointer()) return;
    var r = num(el, 'radius', 80), k = num(el, 'strength', 0.35), cap = num(el, 'shift', 5);
    var xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3' });
    var yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3' });
    function move(e) {
      var b = el.getBoundingClientRect();
      var cx = b.left + b.width / 2, cy = b.top + b.height / 2;
      var dx = e.clientX - cx, dy = e.clientY - cy;
      var d = Math.hypot(dx, dy);
      if (d < r + Math.max(b.width, b.height) / 2) {
        var ox = dx * k, oy = dy * k, m = Math.hypot(ox, oy);   // David 2026-10-19: a button shifts at most 5px (data-m-shift)
        if (m > cap) { ox *= cap / m; oy *= cap / m; }
        xTo(ox); yTo(oy);
      } else { xTo(0); yTo(0); }
    }
    function leave() { xTo(0); yTo(0); }
    window.addEventListener('pointermove', move); el.addEventListener('pointerleave', leave);
    cleanups.push(function () { window.removeEventListener('pointermove', move); el.removeEventListener('pointerleave', leave); gsap.set(el, { x: 0, y: 0 }); });
  };

  P.tilt = function (el) {
    if (!finePointer()) return;
    var max = num(el, 'max', 8);
    el.style.transformStyle = 'preserve-3d';
    var rx = gsap.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3' });
    var ry = gsap.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3' });
    gsap.set(el, { transformPerspective: 800 });
    function move(e) {
      var b = el.getBoundingClientRect();
      var px = (e.clientX - b.left) / b.width - 0.5, py = (e.clientY - b.top) / b.height - 0.5;
      rx(-py * max * 2); ry(px * max * 2);
    }
    function leave() { rx(0); ry(0); }
    el.addEventListener('pointermove', move); el.addEventListener('pointerleave', leave);
    cleanups.push(function () { el.removeEventListener('pointermove', move); el.removeEventListener('pointerleave', leave); gsap.set(el, { rotationX: 0, rotationY: 0 }); });
  };

  P.glide = function (el) {
    var hl = el.querySelector('[data-m-highlight]');
    if (!hl) {
      hl = document.createElement('div'); hl.setAttribute('data-m-highlight', '1'); hl.className = 'm-glide-highlight';
      el.appendChild(hl); // LAST child: an inserted first child would take the page's :nth-child grid rules
      cleanups.push(function () { if (hl.parentNode) hl.parentNode.removeChild(hl); });
    }
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    hl.style.position = 'absolute'; hl.style.pointerEvents = 'none'; hl.style.opacity = '0';
    var kids = all(':scope > *:not([data-m-highlight])', el);
    function to(k) {
      var cb = el.getBoundingClientRect(), b = k.getBoundingClientRect();
      gsap.to(hl, { x: b.left - cb.left, y: b.top - cb.top, width: b.width, height: b.height, opacity: 1, duration: 0.45, ease: EASE });
    }
    kids.forEach(function (k) { k.addEventListener('pointerenter', function () { to(k); }); k.addEventListener('focusin', function () { to(k); }); });
    function off() { gsap.to(hl, { opacity: 0, duration: 0.3 }); }
    el.addEventListener('pointerleave', off); el.addEventListener('focusout', off);
    cleanups.push(function () { gsap.set(hl, { opacity: 0 }); });
  };

  P.buddy = function (el) {
    if (!finePointer()) { el.style.display = 'none'; return; }
    var lag = num(el, 'lag', 0.6), ox = num(el, 'offset-x', 28), oy = num(el, 'offset-y', 28);
    gsap.set(el, { position: 'fixed', top: 0, left: 0, pointerEvents: 'none', zIndex: 60, xPercent: -50, yPercent: -50, x: -200, y: -200 });
    el.classList.add('m-buddy-ready');
    cleanups.push(function () { el.classList.remove('m-buddy-ready'); });
    var xTo = gsap.quickTo(el, 'x', { duration: lag, ease: 'power3' });
    var yTo = gsap.quickTo(el, 'y', { duration: lag, ease: 'power3' });
    var lastX = 0, idle = null, inner = el.firstElementChild || el;
    var wobble = gsap.to(inner, { y: -4, duration: 1.2, yoyo: true, repeat: -1, ease: 'sine.inOut', paused: true });
    function move(e) {
      xTo(e.clientX + ox); yTo(e.clientY + oy);
      gsap.to(inner, { scaleX: e.clientX < lastX ? -1 : 1, duration: 0.2, overwrite: 'auto' });
      lastX = e.clientX; wobble.pause();
      clearTimeout(idle); idle = setTimeout(function () { wobble.play(); }, 600);
    }
    function react() { gsap.fromTo(inner, { scale: 0.8, rotation: -12 }, { scale: 1, rotation: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' }); }
    window.addEventListener('pointermove', move); window.addEventListener('pointerdown', react);
    cleanups.push(function () { window.removeEventListener('pointermove', move); window.removeEventListener('pointerdown', react); wobble.kill(); clearTimeout(idle); });
  };

  P.typewrite = function (el) {
    var text = el.getAttribute('data-m-text') || el.textContent;
    el.setAttribute('data-m-text', text);
    var cps = num(el, 'cps', 40), obj = { n: 0 };
    el.textContent = '';
    ScrollTrigger.create({
      trigger: el, start: str(el, 'start', 'top 85%'), once: true,
      onEnter: function () { gsap.to(obj, { n: text.length, duration: text.length / cps, ease: 'none', onUpdate: function () { el.textContent = text.slice(0, Math.round(obj.n)); } }); }
    });
  };

  /* ---------- X-ray ---------- */
  var xrayLayer = null, badge = null, toggle = null;
  function labelFor(el) {
    var m = el.getAttribute('data-m'), f = el.getAttribute('data-footage');
    if (m) {
      var job = el.getAttribute('data-m-job'), g = el.getAttribute('data-m-graphic');
      if (g) return { kind: 'graphic', text: 'graphic · ' + g + (job ? ' · ' + job : ' · NO JOB'), bad: !job };
      return { kind: 'code', text: 'code · ' + m + (job ? ' · ' + job : ' · NO JOB'), bad: !job };
    }
    var credit = el.getAttribute('data-footage-credit');
    return { kind: 'footage', text: 'footage · ' + f + (credit ? ' · ' + credit : ' · NO CREDIT'), bad: !credit };
  }
  function drawXray() {
    if (!xrayLayer) return;
    xrayLayer.innerHTML = '';
    xrayLayer.style.height = document.documentElement.scrollHeight + 'px';
    var sx = window.scrollX, sy = window.scrollY, vw = document.documentElement.clientWidth;
    all('[data-m], [data-footage]').forEach(function (el) {
      if (el.getAttribute('data-m-clone')) return;
      var b = el.getBoundingClientRect();
      if (!b.width && !b.height) return;
      if (b.left + sx >= vw) return; // off-canvas (a marquee clone): never widen the page for it
      var box = document.createElement('div');
      var L = labelFor(el);
      box.className = 'm-xray-box m-xray-' + L.kind + (L.bad ? ' m-xray-bad' : '');
      box.style.cssText = 'left:' + (b.left + sx) + 'px;top:' + (b.top + sy) + 'px;width:' + b.width + 'px;height:' + b.height + 'px';
      var tag = document.createElement('span'); tag.className = 'm-xray-tag'; tag.textContent = L.text;
      box.appendChild(tag); xrayLayer.appendChild(box);
    });
    var c = count();
    badge.textContent = c.code + ' code loops · ' + c.graphic + ' drawn piece' + (c.graphic === 1 ? '' : 's') + ' · 0 credits  |  ' + c.footage + ' footage asset' + (c.footage === 1 ? '' : 's') + (c.cost ? ' · ' + c.cost : '') + (c.missing ? '  |  ' + c.missing + ' MISSING JOB/CREDIT' : '');
  }
  function count() {
    var code = 0, graphic = 0, footage = 0, missing = 0, dollars = 0;
    all('[data-m]').forEach(function (el) { if (el.getAttribute('data-m-clone')) return; if (el.getAttribute('data-m-graphic')) graphic++; else code++; if (!el.getAttribute('data-m-job')) missing++; });
    all('[data-footage]').forEach(function (el) {
      footage++;
      var cr = el.getAttribute('data-footage-credit');
      if (!cr) missing++; else { var mt = cr.match(/\$([0-9.]+)/); if (mt) dollars += parseFloat(mt[1]); }
    });
    return { code: code, graphic: graphic, footage: footage, missing: missing, cost: dollars ? '$' + dollars.toFixed(2) : '' };
  }
  function xray(on) {
    if (on) {
      if (!xrayLayer) {
        xrayLayer = document.createElement('div'); xrayLayer.className = 'm-xray-layer'; document.body.appendChild(xrayLayer);
        badge = document.createElement('div'); badge.className = 'm-xray-badge'; document.body.appendChild(badge);
        window.addEventListener('scroll', drawXray, { passive: true }); window.addEventListener('resize', drawXray);
      }
      xrayLayer.style.display = ''; badge.style.display = ''; drawXray();
    } else if (xrayLayer) { xrayLayer.style.display = 'none'; badge.style.display = 'none'; }
  }

  /* ---------- toggle pill (Off / On / X-ray) ---------- */
  function buildToggle() {
    if (toggle || !document.body.hasAttribute('data-m-toggle')) return;
    toggle = document.createElement('div'); toggle.className = 'm-toggle'; toggle.setAttribute('role', 'group'); toggle.setAttribute('aria-label', 'Motion');
    var lab = document.createElement('span'); lab.className = 'm-toggle-label'; lab.textContent = 'Motion'; toggle.appendChild(lab);
    ['off', 'on', 'xray'].forEach(function (m) {
      var b = document.createElement('button'); b.type = 'button'; b.textContent = m === 'xray' ? 'X-ray' : m[0].toUpperCase() + m.slice(1);
      b.setAttribute('data-mode', m); b.addEventListener('click', function () { mode(m); }); toggle.appendChild(b);
    });
    document.body.appendChild(toggle);
  }
  function paintToggle() {
    if (!toggle) return;
    all('button', toggle).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-mode') === html.getAttribute('data-motion') ? 'true' : 'false'); });
  }

  /* ---------- lifecycle ---------- */
  function init() {
    if (booted) return;
    booted = true;
    mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', function () {
      all('[data-m]').forEach(function (el) {
        if (el.getAttribute('data-m-clone')) return;
        var type = el.getAttribute('data-m');
        if (!el.getAttribute('data-m-job')) console.warn('[motion] no data-m-job on', el);
        var fn = P[type];
        if (!fn) { console.warn('[motion] unknown primitive "' + type + '"', el); return; }
        try { fn(el); } catch (e) { console.error('[motion] ' + type + ' failed', e, el); }
      });
      return function () { cleanups.forEach(function (c) { try { c(); } catch (e) {} }); cleanups = []; };
    });
    ScrollTrigger.refresh();
  }
  function revert() {
    if (!booted) return;
    if (mm) mm.revert();
    mm = null; booted = false;
    all('[data-m-clone]').forEach(function (c) { c.parentNode && c.parentNode.removeChild(c); });
    all('[data-m-cloned]').forEach(function (t) { t.removeAttribute('data-m-cloned'); });
    all('[data-m="typewrite"]').forEach(function (el) { el.textContent = el.getAttribute('data-m-text') || el.textContent; });
    all('[data-m="count"]').forEach(function (el) { var to = el.getAttribute('data-m-to'); if (to !== null) el.textContent = (el.getAttribute('data-m-prefix') || '') + to + (el.getAttribute('data-m-suffix') || ''); });
  }
  function mode(m) {
    html.setAttribute('data-motion', m);
    if (m === 'off') { revert(); xray(false); }
    else { init(); xray(m === 'xray'); if (m === 'xray') setTimeout(drawXray, 900); }
    paintToggle();
    try { sessionStorage.setItem('aria-motion', m); } catch (e) {}
  }
  function startMode() {
    var q = new URLSearchParams(location.search);
    if (q.get('xray') === '1' || q.get('motion') === 'xray') return 'xray';
    if (q.get('motion') === 'off') return 'off';
    try { var s = sessionStorage.getItem('aria-motion'); if (s) return s; } catch (e) {}
    return html.getAttribute('data-motion') || 'on';
  }

  function boot() {
    ensureGsap(function () {
      gsap.registerPlugin(ScrollTrigger);
      buildToggle();
      mode(startMode());
      window.addEventListener('load', function () { ScrollTrigger.refresh(); if (html.getAttribute('data-motion') === 'xray') drawXray(); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.ARIAMotion = { init: init, revert: revert, mode: mode, count: count, xray: xray, primitives: P };
})();
